import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
    Play, Pause, RotateCcw, ChevronsLeft, ChevronsRight, Maximize2, Minimize2, Gauge, Keyboard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const SENTENCE_END = /[.!?\u3002\uFF01\uFF1F]["')\]]?$/;
const WPM_PRESETS = [100, 200, 300, 400, 500, 700, 1000];
const PREFS_KEY = "reading_os_prefs";

const loadPrefs = () => {
    try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}") || {}; }
    catch (e) { return {}; }
};

function orpIndex(word) {
    const clean = (word || "").replace(/[^\p{L}\p{N}]/gu, "");
    const n = clean.length;
    if (n <= 1) return 0;
    if (n <= 5) return 1;
    if (n <= 9) return 2;
    if (n <= 13) return 3;
    return 4;
}

export const RsvpStage = ({
    tokens = [],
    sections = [],
    initialIndex = 0,
    seekTo,
    onProgress,
    onSession,
    onComplete,
}) => {
    // Resume with re-entry context: back up a few words so the reader
    // re-establishes context instead of dropping in cold mid-sentence.
    const resumeStart = initialIndex > 10 ? Math.max(0, initialIndex - 6) : initialIndex;
    const [index, setIndex] = useState(Math.min(resumeStart, Math.max(0, tokens.length - 1)));
    const [playing, setPlaying] = useState(false);
    const [wpm, setWpm] = useState(() => loadPrefs().wpm || 350);
    const [mode, setMode] = useState(() => loadPrefs().mode || "word"); // word | phrase | sentence
    const [chunkSize, setChunkSize] = useState(3);
    const [adaptive, setAdaptive] = useState(() => loadPrefs().adaptive ?? false);
    const [focus, setFocus] = useState(false);

    // Persist reading preferences so a user's trained speed follows them.
    useEffect(() => {
        try { localStorage.setItem(PREFS_KEY, JSON.stringify({ wpm, mode, adaptive })); } catch (e) {}
    }, [wpm, mode, adaptive]);

    const total = tokens.length;
    const timerRef = useRef(null);
    const indexRef = useRef(index);
    const playStartRef = useRef(null);
    const wordsAtStartRef = useRef(0);
    const lastReportRef = useRef(0);

    useEffect(() => { indexRef.current = index; }, [index]);

    // External seek (e.g. from chat citation jump-to-source)
    useEffect(() => {
        if (seekTo && typeof seekTo.index === "number") {
            setPlaying(false);
            setIndex(Math.min(total, Math.max(0, seekTo.index)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seekTo?.ts]);

    const buildFrame = useCallback((startIdx) => {
        if (startIdx >= total) return { words: [], next: total };
        if (mode === "word") return { words: [tokens[startIdx]], next: startIdx + 1 };
        if (mode === "phrase") {
            const end = Math.min(startIdx + chunkSize, total);
            return { words: tokens.slice(startIdx, end), next: end };
        }
        let end = startIdx;
        const cap = Math.min(startIdx + 14, total);
        while (end < cap) {
            const w = tokens[end];
            end++;
            if (SENTENCE_END.test(w)) break;
        }
        return { words: tokens.slice(startIdx, end), next: end };
    }, [mode, chunkSize, tokens, total]);

    const currentFrame = useMemo(() => buildFrame(index), [buildFrame, index]);

    // Playback loop
    useEffect(() => {
        if (!playing) return;
        if (index >= total) {
            setPlaying(false);
            onComplete?.();
            return;
        }
        const frame = buildFrame(index);
        const wordsCount = Math.max(1, frame.words.length);
        const base = 60000 / wpm;
        let mult = 1;
        if (adaptive && frame.words.length) {
            const last = frame.words[frame.words.length - 1];
            const joined = frame.words.join("");
            const avgLen = joined.length / wordsCount;
            if (avgLen > 7) mult += 0.35;
            if (/[,;:]$/.test(last)) mult += 0.25;
            if (SENTENCE_END.test(last)) mult += 0.5;
        }
        const delay = base * wordsCount * mult;
        // Warm-up ramp: ease from ~65% speed up to target over the first ~6
        // words after pressing play, so the eye can lock onto the focus point.
        const sincePlay = index - wordsAtStartRef.current;
        let ramp = 1;
        if (sincePlay >= 0 && sincePlay < 6) ramp = 1.55 - (sincePlay / 6) * 0.55;
        timerRef.current = setTimeout(() => setIndex(frame.next), delay * ramp);
        return () => clearTimeout(timerRef.current);
    }, [playing, index, wpm, adaptive, buildFrame, total, onComplete]);

    // Progress reporting (throttled)
    useEffect(() => {
        const pct = total ? (index / total) * 100 : 0;
        if (Math.abs(index - lastReportRef.current) >= 12 || index >= total) {
            lastReportRef.current = index;
            onProgress?.(index, pct);
        }
    }, [index, total, onProgress]);

    const flushSession = useCallback(() => {
        if (playStartRef.current == null) return;
        const dur = (Date.now() - playStartRef.current) / 1000;
        const wr = Math.max(0, indexRef.current - wordsAtStartRef.current);
        playStartRef.current = null;
        if (dur > 1 && wr > 0) onSession?.({ wordsRead: wr, durationSec: dur, wpm, mode: "rsvp" });
    }, [onSession, wpm]);

    // Session tracking on play/pause
    useEffect(() => {
        if (playing) {
            playStartRef.current = Date.now();
            wordsAtStartRef.current = indexRef.current;
        } else {
            flushSession();
        }
    }, [playing, flushSession]);

    // Flush on unmount
    useEffect(() => () => flushSession(), [flushSession]);

    const togglePlay = useCallback(() => {
        if (index >= total) { setIndex(0); }
        setPlaying((p) => !p);
    }, [index, total]);

    const rewind = useCallback(() => setIndex((i) => Math.max(0, i - 8)), []);
    const skip = useCallback(() => setIndex((i) => Math.min(total, i + 8)), [total]);
    const restart = useCallback(() => { setIndex(0); }, []);

    // Keyboard shortcuts
    useEffect(() => {
        const onKey = (e) => {
            if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
            if (e.code === "Space") { e.preventDefault(); togglePlay(); }
            else if (e.code === "ArrowLeft") { e.preventDefault(); rewind(); }
            else if (e.code === "ArrowRight") { e.preventDefault(); skip(); }
            else if (e.key === "f" || e.key === "F") { setFocus((v) => !v); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [togglePlay, rewind, skip]);

    const pct = total ? (index / total) * 100 : 0;
    const minutesLeft = wpm ? Math.max(0, Math.round((total - index) / wpm)) : 0;

    const activeSection = useMemo(() => {
        let cur = null;
        for (const s of sections) {
            if (s.word_start <= index) cur = s; else break;
        }
        return cur;
    }, [sections, index]);

    const renderWord = () => {
        const words = currentFrame.words;
        if (!words.length) {
            return <div className="font-sans-display text-2xl text-t3">End of document</div>;
        }
        if (mode === "word") {
            const w = words[0] || "";
            const p = orpIndex(w);
            return (
                <div className="grid w-full max-w-3xl grid-cols-[1fr_auto_1fr] items-baseline" data-testid="rsvp-word">
                    <span className="text-right font-sans-display text-[56px] font-semibold tracking-tight text-t1 sm:text-[80px] md:text-[100px]">{w.slice(0, p)}</span>
                    <span className="text-center font-sans-display text-[56px] font-semibold tracking-tight text-[hsl(var(--primary))] sm:text-[80px] md:text-[100px]">{w[p] || ""}</span>
                    <span className="text-left font-sans-display text-[56px] font-semibold tracking-tight text-t1 sm:text-[80px] md:text-[100px]">{w.slice(p + 1)}</span>
                </div>
            );
        }
        return (
            <div data-testid="rsvp-word" className="max-w-3xl text-center font-sans-display text-3xl font-semibold leading-snug tracking-tight text-t1 sm:text-4xl md:text-5xl">
                {words.join(" ")}
            </div>
        );
    };

    return (
        <div className={cn(focus ? "fixed inset-0 z-50 bg-surface-0 p-3 sm:p-6" : "")}>
            <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-surface-1 noise-overlay">
                <div className="reader-vignette pointer-events-none absolute inset-0" />

                {/* Top meta */}
                <div className="relative flex items-center justify-between px-4 pt-4">
                    <div className="mono-label text-t4">
                        {activeSection ? activeSection.title : "Reading"}
                    </div>
                    <div className="flex items-center gap-1">
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button data-testid="rsvp-shortcuts-button" variant="ghost" size="icon" className="text-t3" title="Keyboard shortcuts">
                                    <Keyboard className="h-4 w-4" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-56 border-border bg-surface-2 text-t2">
                                <div className="mb-2 font-sans-display text-xs font-semibold text-t1">Shortcuts</div>
                                <div className="space-y-1.5 text-xs">
                                    {[["Space", "Play / Pause"], ["\u2190", "Rewind"], ["\u2192", "Skip ahead"], ["F", "Focus mode"]].map(([k, v]) => (
                                        <div key={k} className="flex items-center justify-between">
                                            <span className="text-t3">{v}</span>
                                            <kbd className="rounded bg-surface-3 px-1.5 py-0.5 font-mono-ui text-[10px] text-t1">{k}</kbd>
                                        </div>
                                    ))}
                                </div>
                            </PopoverContent>
                        </Popover>
                        <Button data-testid="rsvp-focus-toggle" variant="ghost" size="icon" className="text-t3" onClick={() => setFocus((v) => !v)}>
                            {focus ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                        </Button>
                    </div>
                </div>

                {/* Stage */}
                <div className="relative flex flex-1 items-center justify-center px-4 py-10" style={{ minHeight: focus ? "auto" : 280 }}>
                    {/* center guide */}
                    <div className="pointer-events-none absolute left-1/2 top-[26%] h-3 w-px -translate-x-1/2 bg-[hsl(var(--primary))]/50" />
                    <div className="pointer-events-none absolute left-1/2 bottom-[26%] h-3 w-px -translate-x-1/2 bg-[hsl(var(--primary))]/50" />
                    {renderWord()}
                </div>

                {/* Progress scrubber */}
                <div className="relative px-5">
                    <Slider
                        data-testid="rsvp-seek-slider"
                        value={[index]}
                        max={Math.max(1, total)}
                        step={1}
                        onValueChange={(v) => setIndex(v[0])}
                    />
                    <div className="mt-1.5 flex items-center justify-between font-mono-ui text-[11px] text-t4">
                        <span>{Math.round(pct)}%</span>
                        <span>{index.toLocaleString()} / {total.toLocaleString()} words</span>
                        <span>~{minutesLeft} min left</span>
                    </div>
                </div>

                {/* Controls dock */}
                <div className="relative mt-3 border-t border-border bg-surface-1/70 px-4 py-3 backdrop-blur-md">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-1.5">
                            <Button data-testid="rsvp-restart-button" variant="ghost" size="icon" className="text-t3" onClick={restart}><RotateCcw className="h-4 w-4" /></Button>
                            <Button data-testid="rsvp-rewind-button" variant="ghost" size="icon" className="text-t2" onClick={rewind}><ChevronsLeft className="h-5 w-5" /></Button>
                            <Button data-testid="rsvp-play-pause-button" size="icon" className="h-11 w-11 rounded-full" onClick={togglePlay}>
                                {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                            </Button>
                            <Button data-testid="rsvp-skip-button" variant="ghost" size="icon" className="text-t2" onClick={skip}><ChevronsRight className="h-5 w-5" /></Button>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                                <Gauge className="h-4 w-4 text-t4" />
                                <div className="w-28">
                                    <Slider data-testid="rsvp-wpm-slider" value={[wpm]} min={100} max={1500} step={25} onValueChange={(v) => setWpm(v[0])} />
                                </div>
                                <span className="w-16 font-mono-ui text-xs text-t2">{wpm} wpm</span>
                            </div>
                        </div>
                    </div>

                    {/* Secondary controls */}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-1">
                            {WPM_PRESETS.map((p) => (
                                <button
                                    key={p}
                                    data-testid={`rsvp-preset-${p}`}
                                    onClick={() => setWpm(p)}
                                    className={cn(
                                        "rounded-md px-2 py-1 font-mono-ui text-[11px] transition-colors",
                                        wpm === p ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "bg-surface-3 text-t3 hover:text-t1"
                                    )}
                                >{p}</button>
                            ))}
                        </div>

                        <div className="ml-auto flex flex-wrap items-center gap-2">
                            <Select value={mode} onValueChange={setMode}>
                                <SelectTrigger data-testid="rsvp-mode-select" className="h-8 w-[120px] bg-surface-2 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="word">Single word</SelectItem>
                                    <SelectItem value="phrase">Phrase</SelectItem>
                                    <SelectItem value="sentence">Sentence</SelectItem>
                                </SelectContent>
                            </Select>

                            {sections.length > 1 && (
                                <Select value={activeSection ? String(activeSection.word_start) : ""} onValueChange={(v) => setIndex(Number(v))}>
                                    <SelectTrigger data-testid="rsvp-chapter-select" className="h-8 w-[150px] bg-surface-2 text-xs"><SelectValue placeholder="Jump to…" /></SelectTrigger>
                                    <SelectContent className="max-h-64">
                                        {sections.map((s, i) => (
                                            <SelectItem key={i} value={String(s.word_start)}>{s.title?.slice(0, 32) || `Section ${i + 1}`}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}

                            <div className="flex items-center gap-1.5 rounded-md bg-surface-2 px-2.5 py-1.5">
                                <span className="mono-label text-t4">Adaptive</span>
                                <Switch data-testid="rsvp-adaptive-switch" checked={adaptive} onCheckedChange={setAdaptive} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
